import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from 'rxjs';
import { PhdsoftCommonService } from "@phdsoft/common";
import { environment } from "src/environments/environment";
import { SensorModel } from 'projects/phdsoft/common/src/lib/models/sensor.model';
import { SensorTypeModel } from 'projects/phdsoft/common/src/lib/models/sensor-type.model';
import { SensorDeviceModel } from 'projects/phdsoft/common/src/lib/models/sensor-device.model';
import { SensorAssetModel } from 'projects/phdsoft/common/src/lib/models/sensor-asset.model';
import { SensorAlarmModel } from 'projects/phdsoft/common/src/lib/models/sensor-alarm.model'
import { SensorStatusModel } from 'projects/phdsoft/common/src/lib/models/sensor-status.model';
import { SensorDataRecord } from 'projects/phdsoft/common/src/lib/models/sensor-datarecord';
import { SensorTimeSeries } from 'projects/phdsoft/common/src/lib/models/sensor-timeseries';
import { EventAnalysisModel } from '../pages/sensors/sensor-processing/models/event-analysis.model';
import { SensorEvent } from 'projects/phdsoft/common/src/lib/models/sensor-event.model';

const sensorBaseUrl = environment.IOT_API+'Sensor';
const typeBaseUrl = environment.IOT_API+'SensorModel';
const deviceBaseUrl = environment.IOT_API+'Device';
const assetBaseUrl = environment.IOT_API+'AssetIoT';
const statusBaseUrl = environment.IOT_API+'AlarmStatus';
const alarmsBaseUrl = environment.IOT_API+'Alarms';
const timeHistoryBaseUrl = environment.IOT_API+'TimeHistory';
const eventBaseUrl = environment.IOT_API+'SensorEvent';
const evtAnalysisBaseUrl = environment.IOT_API+'EventAnalysis';


@Injectable({
  providedIn: 'root'
})

export class SensorsService {

  constructor(
    private phdsoftCommonService: PhdsoftCommonService,
    private http: HttpClient,
  ) { }

  //Sensor Services

  getAssetSensors(assetId : string): Observable<SensorModel[]> {
    return this.http.get<SensorModel[]>(`${sensorBaseUrl}/${assetId}`);
  }

  getSensor(assetId : string, sensorId: string): Observable<SensorModel> {
    return this.http.get<SensorModel>(`${sensorBaseUrl}/${assetId}/${sensorId}`);
  }

  createSensor(sensor : SensorModel): Observable<SensorModel> {
    return this.http.post<SensorModel>(`${sensorBaseUrl}`, sensor);
  }

  updateSensor(sensor : SensorModel): Observable<any> {
    return this.http.put<any>(`${sensorBaseUrl}`, sensor);
  }

  deleteSensor(assetId : string, sensorId : string): Observable<any> {
    return this.http.delete<any>(`${sensorBaseUrl}/${assetId}/${sensorId}`);
  }

  //Sensor Model/Type Services

  createSensorType(sensorType : SensorTypeModel) : Observable<SensorModel> {
    return this.http.post<SensorTypeModel>(`${typeBaseUrl}`, sensorType);
  }

  getSensorType(typeId: string): Observable<SensorModel> {
    return this.http.get<SensorModel>(`${typeBaseUrl}/${typeId}`);
  }

  getSensorTypes(): Observable<SensorTypeModel[]> {
    return this.http.get<SensorTypeModel[]>(typeBaseUrl);
  }

  updateSensorType(sensor : SensorModel): Observable<any> {
    return this.http.put<any>(`${typeBaseUrl}`, sensor);
  }

  deleteSensorType(typeId: string): Observable<any> {
    return this.http.delete<any>(`${typeBaseUrl}/${typeId}`)
  }

  //Device Services

  getAssetDevices(assetId: string): Observable<SensorDeviceModel[]> {
    return this.http.get<SensorDeviceModel[]>(`${deviceBaseUrl}/${assetId}`);
  }

  getDevice(assetId: string, id: string): Observable<SensorDeviceModel> {
    return this.http.get<SensorDeviceModel>(`${deviceBaseUrl}/${assetId}/${id}`);
  }

  createDevice(data: any): Observable<any> {
    return this.http.post(deviceBaseUrl, data);
  }

  updateDevice(data: any): Observable<any> {
    return this.http.put(`${deviceBaseUrl}`, data);
  }

  deleteDevice(assetId : string, deviceId: string): Observable<any> {
    return this.http.delete(`${deviceBaseUrl}/${assetId}/${deviceId}`);
  }

  setDeviceRealTimeProp(assetId:string, deviceId:string, enableRealTime:boolean): Observable<any>{
    return this.http.post(`${deviceBaseUrl}/${assetId}/${deviceId}/${enableRealTime}`,null);
  }


  //Asset Services
  getAssets(): Observable<SensorAssetModel[]> {
    return this.http.get<SensorAssetModel[]>(assetBaseUrl);
  }

  getAsset(id: any): Observable<SensorAssetModel> {
    return this.http.get<SensorAssetModel>(`${assetBaseUrl}/${id}`);
  }

  createAsset(data: any): Observable<any> {
    return this.http.post(assetBaseUrl, data);
  }

  updateAsset(id: any, data: any): Observable<any> {
    return this.http.put(`${assetBaseUrl}/${id}`, data);
  }

  deleteAsset(id: any): Observable<any> {
    return this.http.delete(`${assetBaseUrl}/${id}`);
  }

  //Alarm Definition

  getAssetAlarms(assetId: string) : Observable<SensorAlarmModel[]> {
    return this.http.get<SensorAlarmModel[]>(`${alarmsBaseUrl}/${assetId}`);
  }

  getSensorAlarm(assetId: string, sensorId: string) : Observable<SensorAlarmModel> {
    return this.http.get<SensorAlarmModel>(`${alarmsBaseUrl}/${assetId}/${sensorId}`);
  }

  createAlarm(data: any):Observable<any> {
    return this.http.post(alarmsBaseUrl, data);
  }

  updateAlarm(data: any): Observable<any> {
    return this.http.put(alarmsBaseUrl, data);
  }

  deleteAlarm(assetId : string, sensorId : string): Observable<any> {
    return this.http.delete<any>(`${alarmsBaseUrl}/${assetId}/${sensorId}`);
  }

  //Alarm Status
  getSensorStatus(assetId: string, sensorId: string) : Observable<SensorStatusModel>{
    return this.http.get<SensorStatusModel>(`${statusBaseUrl}/${assetId}/${sensorId}`);
  }

  getAssetStatus(assetId: string) : Observable<SensorStatusModel[]>{
    return this.http.get<SensorStatusModel[]>(`${statusBaseUrl}/${assetId}`)
  }

  //Sensor Time History
  getTimeHistory(customerId:string, assetId:string, sensorId:string, startDate:Date, endDate:Date ) : Observable<SensorDataRecord[]>{
    const dateParams = new HttpParams()
      .set('startDate',startDate.toUTCString())
      .set('endDate', endDate.toUTCString());
    return this.http.get<SensorDataRecord[]>(`${timeHistoryBaseUrl}/${customerId}/${assetId}/${sensorId}`, {params: dateParams})
  }

  getTrendData(assetId: string, sensorId: string, startDate:Date, endDate:Date) : Observable<SensorTimeSeries> {
    const dateParams = new HttpParams()
      .set('startDate',startDate.toUTCString())
      .set('endDate', endDate.toUTCString()); 
    return this.http.get<SensorTimeSeries>(`${timeHistoryBaseUrl}/Trend/${assetId}/${sensorId}`, {params: dateParams})
  }

  //Sensor 3d Model
  getSensorModel(): Observable<string> {
    return this.http.get(`${sensorBaseUrl}/model`, {responseType: 'text'});
  }

  // Event

  getEventList(deviceId: string): Observable<SensorEvent[]>{
    return this.http.get<SensorEvent[]>(`${eventBaseUrl}/${deviceId}`)
  }

  getSensorEvent(assetId:string, sensorId:string, eventId:string):Observable<SensorTimeSeries>{
    return this.http.get<SensorTimeSeries>(`${eventBaseUrl}/${assetId}/${sensorId}/${eventId}`)
  }


  //Event Analysis

  getEventAnalysis(assetId: string, sensorId: string): Observable<EventAnalysisModel>{
    return this.http.get<EventAnalysisModel>(`${evtAnalysisBaseUrl}/${assetId}/${sensorId}`);
  }

  createEventAnalysis(eventAnalysis: EventAnalysisModel): Observable<any>{
    return this.http.post(evtAnalysisBaseUrl,eventAnalysis);
  }

  updateEventAnalysis(eventAnalysis: EventAnalysisModel): Observable<any>{
    return this.http.put(evtAnalysisBaseUrl, eventAnalysis);
  }

}
